import { Dialog } from "@headlessui/react";
import { useState } from "react";
import { Button, ButtonVariant, Modal, Radio } from "shared/components";
import { EnrollmentOptions } from "shared/constants/enrollmentOptions";
import { useAuthContext } from "providers/Auth";
import { EnrollmentOption } from "types/landlord/enrollment";

type Props = {
  rentersCount?: number;
  renterName?: string;
  isOpen: boolean;
  setIsOpen: (value: boolean) => void;
  enrollRenters: (enrollmentOption: EnrollmentOption) => void;
  rentersStateCounts: string[];
  defaultSelectedEnrollmentType?: keyof typeof EnrollmentOptions;
};

export const SubscribeModal = ({
  rentersCount,
  renterName,
  isOpen: open,
  setIsOpen,
  enrollRenters,
  rentersStateCounts,
  defaultSelectedEnrollmentType = "optOut"
}: Props) => {
  const { auth } = useAuthContext();

  const [enrollmentOption, setEnrollmentOption] = useState(
    EnrollmentOptions[defaultSelectedEnrollmentType]
  );

  const optOutDays = auth?.partner.opt_out_period_days || 7;

  const optInDays = auth?.partner.opt_in_period_days || 30;

  const unexpired_opt_in_period = auth?.partner.unexpired_opt_in_period;

  return (
    <Modal
      opened={open}
      onClose={() => setIsOpen(false)}
      panelClassName="overflow-auto p-6 max-w-[445px] w-full"
    >
      <Dialog.Title className="text-xl font-bold text-content-1000 text-center">
        {rentersCount
          ? `You are about to subscribe ${rentersCount} renter(s)`
          : `You are going to subscribe ${renterName}`}
      </Dialog.Title>
      <Dialog.Description className="mt-2 border-b border-alpha-40 pb-6 text-center text-sm font-normal text-content-500">
        This will take about a few minutes to finish. You can subscribe
        <br />
        more renters when subscription is finished.
      </Dialog.Description>

      <div className="mt-6">
        <p className="mb-4 text-sm font-medium text-content-200">
          Renter consent option
        </p>

        {rentersStateCounts.length ? (
          <div className="mt-4 flex gap-2">
            <Radio
              id="isDefaultEnrollment"
              name="isDefaultEnrollment"
              className="items-start"
              checked={enrollmentOption.isDefaultEnrollment}
              onChange={() => {
                setEnrollmentOption(EnrollmentOptions.isDefaultEnrollment);
              }}
              label={
                <label htmlFor="isDefaultEnrollment">
                  <p className="text-sm font-medium text-content-700">
                    Default enrollment type
                  </p>
                  <p className="text-sm text-content-400">
                    {rentersStateCounts.map((count, index) => (
                      <span key={index}>
                        {count}
                        {index < rentersStateCounts.length - 1 && (
                          <span className="mx-2 text-content-300">•</span>
                        )}
                      </span>
                    ))}
                  </p>
                </label>
              }
            />
          </div>
        ) : null}

        <div className="mt-4 flex gap-2">
          <Radio
            id="optIn"
            name="optIn"
            className="items-start"
            checked={enrollmentOption.optIn}
            onChange={() => {
              setEnrollmentOption(EnrollmentOptions.optIn);
            }}
            label={
              <label htmlFor="optIn">
                <p className="text-sm font-medium text-content-700">
                  Renters can opt in
                </p>

                <p className="text-sm text-content-400">
                  {unexpired_opt_in_period
                    ? "Renters are notified via email and can opt in at anytime."
                    : `Renters are notified via email and have ${optInDays} days to opt in`}
                </p>
              </label>
            }
          />
        </div>

        <div className="mt-4 flex gap-2">
          <Radio
            id="optOut"
            name="optOut"
            className="items-start"
            checked={enrollmentOption.optOut}
            onChange={() => {
              setEnrollmentOption(EnrollmentOptions.optOut);
            }}
            label={
              <label htmlFor="optOut">
                <p className="text-sm font-medium text-content-700">
                  Renters can opt out
                </p>

                <p className="text-sm text-content-400">
                  Renters are notified via email and have {optOutDays} days to
                  opt out
                </p>
              </label>
            }
          />
        </div>

        <div className="mt-4 flex gap-2">
          <Radio
            id="optInNow"
            name="optInNow"
            className="items-start"
            checked={
              !(
                enrollmentOption.optIn ||
                enrollmentOption.optOut ||
                enrollmentOption.isDefaultEnrollment
              )
            }
            onChange={() => {
              setEnrollmentOption(EnrollmentOptions.optInNow);
            }}
            label={
              <label htmlFor="optInNow">
                <p className="text-sm font-medium text-content-700">Required</p>

                <p className="text-sm text-content-400">
                  Renters are enrolled with no option to opt in or out.
                </p>
              </label>
            }
          />
        </div>
      </div>

      <div className="mt-6 flex w-full gap-2">
        <div className="w-full">
          <Button
            variant={ButtonVariant.BASE}
            onClick={() => {
              setIsOpen(false);
            }}
            className="w-full"
          >
            Cancel
          </Button>
        </div>
        <div className="w-full">
          <Button
            className="w-full"
            variant={ButtonVariant.PRIMARY}
            onClick={() => {
              enrollRenters(enrollmentOption);
              setIsOpen(false);
            }}
          >
            Proceed
          </Button>
        </div>
      </div>
    </Modal>
  );
};
